import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Statistic,
  Space,
  Typography,
  Spin
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  LockOutlined,
  LogoutOutlined,
  PlusOutlined,
  EditOutlined,
  FileTextOutlined,
  ClearOutlined,
  FilterOutlined
} from '@ant-design/icons';
import './Logs.css';
import fundoNovagGeracao from '../images/FUNDO_NOVAGERACAO_SEMPRE.png';
import { buildApiUrl } from '../utils/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title, Paragraph } = Typography;

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [filtros, setFiltros] = useState({
    tipo: '',
    dataInicio: '',
    dataFim: '',
    usuario: '',
    acao: ''
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/admin/logs'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
      } else {
        console.error('Erro ao buscar logs');
      }
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTipoColor = (tipo) => {
    switch (tipo.toLowerCase()) {
      case 'pedido':
        return '#28a745'; // Verde
      case 'produto':
        return '#007bff'; // Azul
      case 'usuario':
        return '#ffc107'; // Amarelo
      case 'sistema':
        return '#6c757d'; // Cinza
      default:
        return '#6c757d';
    }
  };

  const getAcaoIcon = (acao) => {
    switch (acao.toLowerCase()) {
      case 'criar':
      case 'criado':
        return <PlusOutlined />;
      case 'editar':
      case 'editado':
        return <EditOutlined />;
      case 'excluir':
      case 'excluído':
        return <DeleteOutlined />;
      case 'finalizar':
      case 'finalizado':
        return <CheckCircleOutlined />;
      case 'cancelar':
      case 'cancelado':
        return <CloseCircleOutlined />;
      case 'login':
        return <LockOutlined />;
      case 'logout':
        return <LogoutOutlined />;
      default:
        return <FileTextOutlined />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  // Função para filtrar logs
  const logsFiltrados = logs.filter(log => {
    // Filtro por tipo
    if (filtros.tipo && log.tipo.toLowerCase() !== filtros.tipo.toLowerCase()) {
      return false;
    }
    
    // Filtro por ação
    if (filtros.acao && !log.acao.toLowerCase().includes(filtros.acao.toLowerCase())) {
      return false;
    }
    
    // Filtro por usuário
    if (filtros.usuario && !log.usuario.toLowerCase().includes(filtros.usuario.toLowerCase())) {
      return false;
    }
    
    // Filtro por data
    if (filtros.dataInicio || filtros.dataFim) {
      const dataLog = new Date(log.created_at);
      
      if (filtros.dataInicio) {
        const [ano, mes, dia] = filtros.dataInicio.split('-').map(Number);
        const dataInicio = new Date(ano, mes - 1, dia, 0, 0, 0, 0);
        
        if (dataLog < dataInicio) {
          return false;
        }
      }
      
      if (filtros.dataFim) {
        const [ano, mes, dia] = filtros.dataFim.split('-').map(Number);
        const dataFim = new Date(ano, mes - 1, dia, 23, 59, 59, 999);
        
        if (dataLog > dataFim) {
          return false;
        }
      }
    }
    
    return true;
  });

  // Função para limpar filtros
  const limparFiltros = () => {
    setFiltros({
      tipo: '',
      dataInicio: '',
      dataFim: '',
      usuario: '',
      acao: ''
    });
  };

  // Estatísticas
  const totalLogs = logs.length;
  const logsPedidos = logs.filter(l => l.tipo === 'Pedido').length;
  const logsProdutos = logs.filter(l => l.tipo === 'Produto').length;
  const logsUsuarios = logs.filter(l => l.tipo === 'Usuario').length;
  const logsSistema = logs.filter(l => l.tipo === 'Sistema').length;

  // Colunas da tabela do Ant Design
  const columns = [
    {
      title: 'Data/Hora',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => formatDate(text),
      width: 180,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      render: (tipo) => (
        <Tag color={getTipoColor(tipo)}>{tipo}</Tag>
      ),
      width: 120,
      filters: [
        { text: 'Pedido', value: 'Pedido' },
        { text: 'Produto', value: 'Produto' },
        { text: 'Usuario', value: 'Usuario' },
        { text: 'Sistema', value: 'Sistema' }
      ],
      onFilter: (value, record) => record.tipo === value
    },
    {
      title: 'Ação',
      dataIndex: 'acao',
      key: 'acao',
      render: (acao) => (
        <Space>
          {getAcaoIcon(acao)}
          <span>{acao}</span>
        </Space>
      ),
      width: 150
    },
    {
      title: 'Usuário',
      dataIndex: 'usuario',
      key: 'usuario',
      width: 200
    },
    {
      title: 'Detalhes',
      dataIndex: 'detalhes',
      key: 'detalhes',
      render: (detalhes) => detalhes.replace(/Enviado/g, 'Em Transporte')
    }
  ];

  if (loading) {
    return (
      <Space direction="vertical" size={24} style={{ width: '100%', padding: '24px', background: '#fff', minHeight: '100vh' }}>
        <Space direction="vertical" align="center" style={{ width: '100%', padding: '100px 0' }}>
          <Spin size="large" />
          <Paragraph>Carregando logs...</Paragraph>
        </Space>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={24} style={{ width: '100%', padding: '24px', background: '#fff', minHeight: '100vh' }}>
      {/* Header com Background */}
      <Card
        bordered={false}
        bodyStyle={{
          padding: 0,
          background: `linear-gradient(135deg, rgba(62, 121, 247, 0.95) 0%, rgba(30, 90, 220, 0.95) 100%)`,
          backgroundImage: `url(${fundoNovagGeracao})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          borderRadius: '8px',
          minHeight: '140px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Row justify="center" align="middle" style={{ width: '100%', padding: '40px 40px' }}>
          <Col style={{ textAlign: 'center' }}>
            <Space direction="vertical" size={12}>
              <Title level={1} style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: '42px' }}>
                <FileTextOutlined style={{ marginRight: '16px' }} />
                Gerenciamento de Logs
              </Title>
              <Paragraph style={{ color: 'rgba(255, 255, 255, 0.95)', margin: 0, fontSize: '20px', fontWeight: 400 }}>
                Visualize todas as atividades do sistema
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Estatísticas */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={12} lg={4}>
          <Card>
            <Statistic
              title="Total de Logs"
              value={totalLogs}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={5}>
          <Card>
            <Statistic
              title="Pedidos"
              value={logsPedidos}
              valueStyle={{ color: '#28a745' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={5}>
          <Card>
            <Statistic
              title="Produtos"
              value={logsProdutos}
              valueStyle={{ color: '#007bff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={5}>
          <Card>
            <Statistic
              title="Usuários"
              value={logsUsuarios}
              valueStyle={{ color: '#ffc107' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={5}>
          <Card>
            <Statistic
              title="Sistema"
              value={logsSistema}
              valueStyle={{ color: '#6c757d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card 
        title={<><FilterOutlined /> Filtros</>}
        extra={
          <Button 
            icon={<ClearOutlined />}
            onClick={limparFiltros}
          >
            Limpar
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <label>Tipo:</label>
              <Select
                style={{ width: '100%' }}
                placeholder="Todos os tipos"
                value={filtros.tipo || undefined}
                onChange={(value) => setFiltros({...filtros, tipo: value || ''})}
                allowClear
              >
                <Option value="Pedido">Pedido</Option>
                <Option value="Produto">Produto</Option>
                <Option value="Usuario">Usuário</Option>
                <Option value="Sistema">Sistema</Option>
              </Select>
            </Space>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <label>Ação:</label>
              <Input
              placeholder="Digite a ação..."
              value={filtros.acao}
              onChange={(e) => setFiltros({...filtros, acao: e.target.value})}
                allowClear
            />
            </Space>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <label>Usuário:</label>
              <Input
              placeholder="Digite o usuário..."
              value={filtros.usuario}
              onChange={(e) => setFiltros({...filtros, usuario: e.target.value})}
                allowClear
            />
            </Space>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <label>Data Início:</label>
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Data início"
                value={filtros.dataInicio ? dayjs(filtros.dataInicio) : null}
                onChange={(date) => setFiltros({...filtros, dataInicio: date ? date.format('YYYY-MM-DD') : ''})}
              />
            </Space>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <label>Data Fim:</label>
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Data fim"
                value={filtros.dataFim ? dayjs(filtros.dataFim) : null}
                onChange={(date) => setFiltros({...filtros, dataFim: date ? date.format('YYYY-MM-DD') : ''})}
              />
            </Space>
          </Col>
        </Row>
        
        <Row style={{ marginTop: 16 }}>
          <Col>
            <Paragraph style={{ margin: 0 }}>
              Mostrando <strong>{logsFiltrados.length}</strong> de <strong>{logs.length}</strong> logs
            </Paragraph>
          </Col>
        </Row>
      </Card>

      {/* Tabela de Logs */}
      <Card>
        <Table
          columns={columns}
          dataSource={logsFiltrados}
          rowKey="id"
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total) => `Total de ${total} logs`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onShowSizeChange: (current, size) => setPageSize(size)
          }}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: 'Nenhum log encontrado'
          }}
        />
      </Card>
    </Space>
  );
};

export default Logs; 
